import React, { useEffect, useState, useCallback } from 'react';
import { FaArrowRight, FaChevronDown } from 'react-icons/fa';
import './Header.css';

const slides = [
  {
    image: `${process.env.PUBLIC_URL}/reshome.jpeg`,
    title: 'Landscaping Maintenance',
    subtitle: 'Beautiful yards tailored for every home',
    cta: 'Explore Services',
    link: '/services/residential-maintenance',
    hasDropdown: true,
    dropdownItems: [
      { text: 'Residential', link: '/services/residential-maintenance' },
      { text: 'HOA', link: '/services/hoa-maintenance' },
      { text: 'Commercial', link: '/services/commercial-maintenance' }
    ]
  },
  {
    image: `${process.env.PUBLIC_URL}/turf-header-image.jpg`,
    title: 'Turf Installation Service',
    subtitle: 'Turf installation for residential and commercial properties',
    cta: 'Discover Options',
    link: '/services/turf-installation'
  },
  {
    image: `${process.env.PUBLIC_URL}/design.jpeg`,
    title: 'Landscape Design',
    subtitle: 'Transform Your Property with a Landscape Design',
    cta: 'Learn More',
    link: '/Landscapingdesign'
  },
];

const ANIMATION_DURATION = 800;

export default function Header() {
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleTransition = useCallback((next) => {
    if (isAnimating || next === current) return;

    setPrev(current);
    setCurrent(next);
    setIsAnimating(true);
    setDropdownOpen(false);

    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, ANIMATION_DURATION);

    return () => clearTimeout(timer);
  }, [current, isAnimating]);

  // Auto-rotation effect
  useEffect(() => {
    if (isHovered) return;

    const timer = setInterval(() => {
      handleTransition((current + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [current, isHovered, handleTransition]);

  // Touch event handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    if (distance > 50) {
      // Swipe left
      handleTransition((current + 1) % slides.length);
    } else if (distance < -50) {
      // Swipe right
      handleTransition((current - 1 + slides.length) % slides.length);
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleIndicatorClick = (index) => {
    handleTransition(index);
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    if (slides[current].hasDropdown) {
      setDropdownOpen(!dropdownOpen);
    } else {
      window.location.href = slides[current].link;
    }
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
  };

  return (
    <header
      className="header"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Background slides */}
      <div
        className={`header__slide ${isAnimating ? 'fade-out' : ''}`}
        style={{ backgroundImage: `url(${slides[prev].image})` }}
        aria-hidden="true"
      />
      <div
        className={`header__slide ${isAnimating ? 'fade-in' : 'visible'}`}
        style={{ backgroundImage: `url(${slides[current].image})` }}
        aria-label={`Slide: ${slides[current].title}`}
      />

      {/* Overlay Content */}
      <div className="header__overlay">
        <div className="header__content">
          <div className={`header__text ${isAnimating ? 'slide-up' : 'slide-down'}`}>
            <p className="header__subtitle">{slides[current].subtitle}</p>
            <h1 className="header__title">{slides[current].title}</h1>
          </div>

          {/* CTA Button with Dropdown */}
          <div 
            className={`header__btn-container ${dropdownOpen ? 'dropdown-open' : ''}`}
            onMouseLeave={closeDropdown}
          >
            <button 
              onClick={toggleDropdown}
              className="btn header__btn"
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
              aria-controls="dropdown-menu"
            >
              {slides[current].cta}
              {slides[current].hasDropdown ? (
                <FaChevronDown className={`btn__icon ${dropdownOpen ? 'rotate' : ''}`} />
              ) : (
                <FaArrowRight className="btn__icon" />
              )}
            </button>
            
            {slides[current].hasDropdown && (
              <div 
                id="dropdown-menu"
                className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`}
                role="menu"
              >
                {slides[current].dropdownItems.map((item, index) => (
                  <a 
                    key={index}
                    href={item.link}
                    className="dropdown-item"
                    onClick={closeDropdown}
                    role="menuitem"
                  >
                    {item.text}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="header__indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              aria-label={`Go to slide ${index + 1}`}
              className={`indicator${index === current ? ' active' : ''}`}
              onClick={() => handleIndicatorClick(index)}
            />
          ))}
        </div>
      </div>
    </header>
  );
}
